import * as ko from "knockout";
import PromiseRequest from "../../req/PromiseRequest";
import {IMessage, IMessageData, IRouteDefinitionParams, IRuleset} from "../../index";
import {nsRoute} from "../../util/route";
import {DELETING_MESSAGE, REQUEST_MESSAGE} from "../../constants/Language";
import {ButtonManager} from "../../util/ButtonManager";
import {getCancelButton, setRouteHash} from "../../constants/RouteDefinitions";
import {global} from "../../appContext";
import {createErrorMessage, isPartOf} from "../../util/misc";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import ButtonOrder = nsRoute.ButtonOrder;
import deleteehubHtml from './deleteehub.html?raw';

export const template = deleteehubHtml;

enum Step {
    Init = 'Init',
    Confirm = 'Confirm',
    Process = 'Process',
    Finish = 'Finish',
}

export class viewModel extends ParsedRouteDataDecorator {
    private Step = Step

    private readonly step: KnockoutObservable<Step | null> = ko.observable(null)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    private readonly req_deletemessage: PromiseRequest = new PromiseRequest('deletemessage')
    private readonly req_deleteruleset: PromiseRequest = new PromiseRequest('deleteruleset')
    private readonly req_getmessage: PromiseRequest = new PromiseRequest('getmessage')
    private readonly req_getruleset: PromiseRequest = new PromiseRequest('getruleset')

    private messageflow!: IMessage;
    private ruleset!: IRuleset;

    private pageButtons: ButtonManager = new ButtonManager()
    private readonly isEHubApplication: boolean;

    private error_messageflow: string | null = null
    private error_ruleset: string | null = null

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.isEHubApplication = global.eHubManager?.isStarted?.() ?? false

        this.pageButtons.add(getCancelButton(() => {
            if (this.isEHubApplication) {
                global.eHubManager.fnReject('abort')
            } else {
                setRouteHash('messageflowsearch');
            }
        }, {
            validator: () => this.isStep(Step.Init, Step.Confirm),
        }))

        this.pageButtons.add({
            onClick: () => {
                this.step_process()
            },
            cssClass: 'btn btn-danger',
            icon: 'fa-trash-alt',
            label: 'yes, continue',
            order: ButtonOrder.DELETE,
            validator: () => this.isStep(Step.Confirm),
        })

        this.pageButtons.add({
            onClick: () => {
                if (this.isEHubApplication) {
                    global.eHubManager.fnResolve(true)
                } else {
                    setRouteHash('messageflowsearch');
                }
            },
            icon: 'fa-check',
            label: 'ok',
            order: ButtonOrder.CONTINUE,
            validator: () => this.isStep(Step.Finish),
        })

        this.pageButtons.add({
            onClick: () => {
                this.step_init()
            },
            icon: 'fa-check',
            label: 'try again',
            order: ButtonOrder.PREVIOUS,
            validator: () => this.isStep(Step.Finish) && (!!this.error_messageflow || !!this.error_ruleset),
        })

        this.step_init();
    }

    private step_init() {
        this.messageData({message: REQUEST_MESSAGE});
        this.step(Step.Init)
        return Promise.resolve()
            .then(() => this.req_getmessage
                .request({id: this.__id})
                .then((data: [IMessage]) => this.messageflow = data[0])
            )
            .then(() => this.req_getruleset
                .request({id: this.messageflow.rulesetid})
                .then((data: [IRuleset]) => this.ruleset = data[0])
            )
            .then(() => {
                this.messageData(null)
                this.step(Step.Confirm)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private step_process() {
        this.messageData({message: DELETING_MESSAGE})
        this.step(Step.Process)

        return Promise.resolve()
            .then(() => this.req_deletemessage
                .request({id: this.messageflow.id})
                .catch((reason: string) => this.error_messageflow = reason)
            )
            .then(() => this.req_deleteruleset
                .request({id: this.messageflow.rulesetid})
                .catch((reason: string) => this.error_ruleset = reason)
            )
            .then(() => {
                this.messageData(null)
                this.step(Step.Finish)
            })
            .catch((reason: string) => {
                this.step(Step.Finish)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    protected isStep = isPartOf.bind(this, this.step)

    override dispose() {
        super.dispose();
        this.req_deletemessage.abort()
        this.req_deleteruleset.abort()
        this.req_getmessage.abort()
        this.req_getruleset.abort()
        if (this.isEHubApplication) {
            global.eHubManager.fnReject('abort')
        }
    }
}
